import { Box, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Page } from '../../components';
import { AdminTable } from '../../components/table/AdminTable';
import { INetsuiteAccountsAPI } from '../../fetch/netsuite-accounts';
import { useToastContext } from '../../hooks';
import { INetSuiteAccounts } from '../../models';

export const NetsuiteAccounts: FC = () => {
  const { showToast } = useToastContext();

  const [netsuiteAcountConfigs, setNetsuiteAccountConfigs] = useState<INetSuiteAccounts[]>([]);
  const [isLoadingNetsuiteAccounts, setIsLoadingNetsuiteAccounts] = useState(false);
  const [isSavingNetsuiteAccounts, setIsSavingNetsuiteAccounts] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchNetsuiteAccountsOnMount = useCallback(async () => {
    setIsLoadingNetsuiteAccounts(true);
    try {
      setNetsuiteAccountConfigs(await INetsuiteAccountsAPI.getAll());
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch activity types.');
    } finally {
      setIsLoadingNetsuiteAccounts(false);
    }
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchNetsuiteAccountsOnMount();
  }, [fetchNetsuiteAccountsOnMount]);

  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Account Type',
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: INetSuiteAccounts; index: number } };
        }) => {
          return (
            <Box>
              <TextField
                fullWidth
                // Activity type is read-only.
                disabled
                placeholder="NetSuite Account Type"
                value={original.netsuiteAccountTypeName}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'accountId',
        Header: 'NetSuite Account ID',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: INetSuiteAccounts; index: number } };
        }) => {
          const [accountId, setAccountId] = useState<null | number>(original.accountId);

          return (
            <Box>
              <NumberFormat
                fullWidth
                isNumericString
                decimalScale={0}
                size="small"
                allowNegative={false}
                customInput={TextField}
                value={accountId}
                onValueChange={values => {
                  const value = parseInt(values.value, 10);
                  if (Number.isNaN(value)) setAccountId(null);
                  setAccountId(value);
                }}
                onBlur={() => {
                  // Write changes
                  setNetsuiteAccountConfigs((state: INetSuiteAccounts[]) => {
                    if (state[index].accountId !== accountId) {
                      setHasChanges(true);
                    }

                    state[index].accountId = accountId;

                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netSuiteDescription',
        Header: 'Account Description',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: INetSuiteAccounts; index: number } };
        }) => {
          const [netSuiteDescription, setNetsuiteDescription] = useState<string>(
            original.accountName
          );

          return (
            <Box>
              <TextField
                fullWidth
                size="small"
                value={netSuiteDescription}
                onChange={(e: any) => {
                  setNetsuiteDescription(e.target.value);
                }}
                onBlur={() => {
                  // Write changes
                  setNetsuiteAccountConfigs((state: INetSuiteAccounts[]) => {
                    if (state[index].accountName !== netSuiteDescription) {
                      setHasChanges(true);
                    }

                    state[index].accountName = netSuiteDescription;

                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
    ];
  }, []);

  return (
    <Page title="NetSuite Account IDs">
      <AdminTable
        data={netsuiteAcountConfigs}
        columns={columns}
        isLoading={isLoadingNetsuiteAccounts}
        hasChanges={hasChanges}
        isSaving={isSavingNetsuiteAccounts}
        onSave={async function upsertActivityTypes() {
          setIsSavingNetsuiteAccounts(true);
          try {
            const updatedList: INetSuiteAccounts[] = await INetsuiteAccountsAPI.upsert(
              netsuiteAcountConfigs
            );
            setNetsuiteAccountConfigs(updatedList);
            setHasChanges(false);
            showToast('success', 'NetSuite Account was updated successfully!');
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update activity types.';
            showToast('error', message);
          } finally {
            setIsSavingNetsuiteAccounts(false);
          }
        }}
      />
    </Page>
  );
};
